import { useState, useEffect, useCallback } from 'react';
import api from '@/services/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import {
  Film, Plus, Trash2, Loader2, ChevronRight, ChevronLeft, Check, Sparkles,
  Play, Download, Save, GripVertical, Copy, RefreshCw, Merge, Eye, Wand2,
  ArrowRight, Clapperboard, Layers, Zap, MonitorPlay, Scissors,
  AlertCircle, CheckCircle2, Clock, ArrowUpDown
} from 'lucide-react';
import { toast } from 'sonner';

const STEPS = [
  { id: 1, label: 'Proje Ayarları', icon: Layers },
  { id: 2, label: 'AI Senaryo', icon: Sparkles },
  { id: 3, label: 'İnceleme', icon: Eye },
  { id: 4, label: 'Üretim', icon: Zap },
  { id: 5, label: 'Sonuçlar', icon: Play },
  { id: 6, label: 'Sıralama', icon: ArrowUpDown },
  { id: 7, label: 'Birleştir', icon: Merge },
  { id: 8, label: 'Final Video', icon: MonitorPlay },
  { id: 9, label: 'Son İşlem', icon: Scissors },
];

const TONES = ['sinematik', 'enerjik', 'duygusal', 'gerilim', 'komedi', 'belgesel', 'epik', 'minimalist'];
const STYLES = ['gerçekçi', 'anime', 'çizgi film', '3D render', 'soyut', 'retro', 'neon', 'doğal'];
const MOTIONS = [
  { id: 'cinematic', label: 'Sinematik' },
  { id: 'fast', label: 'Hızlı' },
  { id: 'social', label: 'Sosyal Medya' },
  { id: 'slow', label: 'Yavaş / Ağır Çekim' },
];
const MODELS = [
  { id: 'kling', model_id: 'kling/v3', label: 'Kling AI v3', available: true },
  { id: 'kling', model_id: 'kling/v2-6', label: 'Kling AI v2.6', available: true },
  { id: 'runway', model_id: 'runway/gen4.5', label: 'Runway Gen 4.5', available: true },
  { id: 'sora', model_id: 'sora/v2', label: 'Sora v2', available: false },
  { id: 'luma', model_id: 'luma/dream', label: 'Luma Dream Machine', available: false },
];
const ASPECT_RATIOS = [
  { id: '16:9', label: 'Yatay (16:9)' },
  { id: '9:16', label: 'Dikey (9:16)' },
  { id: '1:1', label: 'Kare (1:1)' },
];
const CREDIT_PER_SCENE = 5;

export default function LongVideoStudioPage() {
  const [step, setStep] = useState(1);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sceneCount, setSceneCount] = useState(3);
  const [customCount, setCustomCount] = useState('');
  const [aspectRatio, setAspectRatio] = useState('16:9');
  const [projectTitle, setProjectTitle] = useState('');
  const [aiTopic, setAiTopic] = useState('');
  const [aiTone, setAiTone] = useState('sinematik');
  const [aiStyle, setAiStyle] = useState('gerçekçi');
  const [aiInstructions, setAiInstructions] = useState('');
  const [generatingAI, setGeneratingAI] = useState(false);
  const [generatingVideos, setGeneratingVideos] = useState(false);
  const [merging, setMerging] = useState(false);
  const [editPrompts, setEditPrompts] = useState({});
  const [regeneratingScene, setRegeneratingScene] = useState(null);
  const [dragIdx, setDragIdx] = useState(null);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  // Poll project status during generation
  const pollProject = useCallback(async () => {
    if (!project?.id) return false;
    try {
      const r = await api.get(`/long-video/projects/${project.id}`);
      setProject(r.data);
      const scenes = r.data.scenes || [];
      return scenes.every(s => s.status === 'done' || s.status === 'failed');
    } catch { return false; }
  }, [project?.id]);

  useEffect(() => {
    let interval;
    if (generatingVideos && project?.id) {
      interval = setInterval(async () => {
        const done = await pollProject();
        if (done) {
          setGeneratingVideos(false);
          clearInterval(interval);
          const r = await api.get(`/long-video/projects/${project.id}`);
          setProject(r.data);
          if (r.data.scenes.every(s => s.status === 'done')) {
            toast.success('Tüm sahneler üretildi!');
          } else {
            toast.info('Bazı sahneler başarısız oldu. Yeniden deneyebilirsiniz.');
          }
          setStep(5);
        }
      }, 4000);
    }
    return () => clearInterval(interval);
  }, [generatingVideos, project?.id, pollProject]);

  // Step 1: Create project + generate AI scripts
  const handleCreateAndGenerate = async () => {
    const count = customCount ? parseInt(customCount) : sceneCount;
    if (count < 2 || count > 10) { toast.error('Sahne sayısı 2-10 arası olmalı'); return; }
    if (!aiTopic.trim()) { toast.error('Lütfen video konusunu girin'); return; }

    setLoading(true);
    try {
      // Create project
      const r = await api.post('/long-video/projects', {
        title: projectTitle || aiTopic.slice(0, 50),
        scene_count: count,
        aspect_ratio: aspectRatio,
      });
      const proj = r.data;
      setProject(proj);

      // Generate AI scripts
      setGeneratingAI(true);
      const scripts = await api.post(`/long-video/projects/${proj.id}/ai-scripts`, {
        topic: aiTopic, tone: aiTone, style: aiStyle,
        custom_instructions: aiInstructions,
      });
      setProject(prev => ({ ...prev, scenes: scripts.data.scenes }));
      setGeneratingAI(false);
      toast.success('Proje oluşturuldu ve senaryolar yazıldı!');
      setStep(2);
    } catch (err) {
      setGeneratingAI(false);
      toast.error(err.response?.data?.detail || 'İşlem başarısız');
    }
    setLoading(false);
  };

  // Update a scene field locally
  const updateScene = (sceneId, field, value) => {
    if (!project) return;
    setProject(prev => ({
      ...prev,
      scenes: prev.scenes.map(s => s.id === sceneId ? { ...s, [field]: value } : s)
    }));
  };

  // Save scenes to backend
  const saveScenes = async () => {
    if (!project) return;
    try {
      await api.put(`/long-video/projects/${project.id}`, { scenes: project.scenes });
    } catch { }
  };

  // Regenerate single scene AI prompt
  const handleRegenScenePrompt = async (sceneId) => {
    const scene = project.scenes.find(s => s.id === sceneId);
    if (!scene) return;
    setRegeneratingScene(sceneId);
    try {
      const r = await api.post(`/long-video/projects/${project.id}/scenes/${sceneId}/regenerate`, {
        edit_prompt: 'Bu sahne için tamamen yeni bir prompt oluştur, farklı bir perspektif kullan.',
      });
      // Refresh project
      const p = await api.get(`/long-video/projects/${project.id}`);
      setProject(p.data);
      toast.success('Sahne prompt yenilendi');
    } catch (err) {
      toast.error('Prompt yenileme başarısız');
    }
    setRegeneratingScene(null);
  };

  // Generate all videos
  const handleGenerateAll = async () => {
    await saveScenes();
    setGeneratingVideos(true);
    try {
      const r = await api.post(`/long-video/projects/${project.id}/generate-all`);
      toast.success(`${r.data.total_scenes} sahne üretiliyor...`);
      setStep(4);
      const p = await api.get(`/long-video/projects/${project.id}`);
      setProject(p.data);
    } catch (err) {
      setGeneratingVideos(false);
      toast.error(err.response?.data?.detail || 'Üretim başlatılamadı');
    }
  };

  // Regenerate single scene video
  const handleRegenerateVideo = async (sceneId, editPrompt = '') => {
    setRegeneratingScene(sceneId);
    try {
      await api.post(`/long-video/projects/${project.id}/scenes/${sceneId}/regenerate`, {
        edit_prompt: editPrompt,
      });
      toast.success('Sahne yeniden üretiliyor...');
      const poll = setInterval(async () => {
        const r = await api.get(`/long-video/projects/${project.id}`);
        setProject(r.data);
        const scene = r.data.scenes.find(s => s.id === sceneId);
        if (scene && (scene.status === 'done' || scene.status === 'failed')) {
          clearInterval(poll);
          setRegeneratingScene(null);
          if (scene.status === 'done') toast.success('Sahne yeniden üretildi!');
          else toast.error('Sahne üretimi başarısız');
        }
      }, 4000);
    } catch (err) {
      setRegeneratingScene(null);
      toast.error(err.response?.data?.detail || 'Yeniden üretim başarısız');
    }
  };

  // Merge
  const handleMerge = async () => {
    setMerging(true);
    try {
      const r = await api.post(`/long-video/projects/${project.id}/merge`, { transition: 'none' });
      setProject(prev => ({ ...prev, final_video_url: r.data.url, status: 'done' }));
      toast.success('Videolar birleştirildi!');
      setStep(8);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Birleştirme başarısız');
    }
    setMerging(false);
  };

  // Save to library
  const handleSaveToLibrary = async () => {
    try {
      await api.post(`/long-video/projects/${project.id}/save-to-library`);
      toast.success('Medya kütüphanesine kaydedildi!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kaydetme başarısız');
    }
  };

  // Drag & drop
  const handleDragStart = (idx) => setDragIdx(idx);
  const handleDragOver = (e) => e.preventDefault();
  const handleDrop = async (targetIdx) => {
    if (dragIdx === null || dragIdx === targetIdx) return;
    const scenes = [...project.scenes];
    const [moved] = scenes.splice(dragIdx, 1);
    scenes.splice(targetIdx, 0, moved);
    const reindexed = scenes.map((s, i) => ({ ...s, index: i }));
    setProject(prev => ({ ...prev, scenes: reindexed }));
    setDragIdx(null);
    try {
      await api.put(`/long-video/projects/${project.id}/scenes/reorder`, {
        scene_order: reindexed.map(s => s.id),
      });
    } catch { }
  };

  // Add scene
  const handleAddScene = async () => {
    try {
      const r = await api.post(`/long-video/projects/${project.id}/scenes`, {
        title: '', prompt: '', model: 'kling', model_id: 'kling/v3',
      });
      setProject(prev => ({ ...prev, scenes: [...prev.scenes, r.data], scene_count: prev.scene_count + 1 }));
      toast.success('Sahne eklendi');
    } catch { toast.error('Sahne eklenemedi'); }
  };

  // Remove scene
  const handleRemoveScene = async (sceneId) => {
    try {
      await api.delete(`/long-video/projects/${project.id}/scenes/${sceneId}`);
      setProject(prev => ({
        ...prev,
        scenes: prev.scenes.filter(s => s.id !== sceneId),
        scene_count: prev.scene_count - 1,
      }));
      toast.success('Sahne silindi');
    } catch { toast.error('Sahne silinemedi'); }
  };

  // Duplicate scene
  const handleDuplicate = async (sceneId) => {
    try {
      const r = await api.post(`/long-video/projects/${project.id}/scenes/${sceneId}/duplicate`);
      setProject(prev => ({ ...prev, scenes: [...prev.scenes, r.data], scene_count: prev.scene_count + 1 }));
      toast.success('Sahne kopyalandı');
    } catch { toast.error('Kopyalama başarısız'); }
  };

  const scenes = project?.scenes || [];
  const completedScenes = scenes.filter(s => s.status === 'done');
  const generatingScenes = scenes.filter(s => s.status === 'generating');

  return (
    <div data-testid="long-video-studio-page" className="animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between pb-6 border-b border-[#262626] mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500/20 to-amber-600/10 border border-orange-500/20 flex items-center justify-center">
            <Clapperboard size={20} className="text-orange-400" />
          </div>
          <div>
            <h1 className="font-outfit text-3xl font-bold text-white tracking-tight">Uzun Video Stüdyosu</h1>
            <p className="text-[#A3A3A3] font-ibm text-sm mt-0.5">Çoklu sahne ile profesyonel video oluşturun</p>
          </div>
        </div>
        {project && (
          <Badge variant="outline" className="text-xs bg-orange-500/10 text-orange-400 border-orange-500/20 font-jetbrains">
            {scenes.length} sahne | {scenes.length * CREDIT_PER_SCENE} kredi
          </Badge>
        )}
      </div>

      {/* Step Indicator */}
      <div className="mb-8 overflow-x-auto">
        <div className="flex items-center gap-1 min-w-max">
          {STEPS.map((s, i) => {
            const isActive = step === s.id;
            const isDone = step > s.id;
            return (
              <div key={s.id} className="flex items-center">
                <button
                  onClick={() => { if (isDone) setStep(s.id); }}
                  disabled={!isDone && !isActive}
                  data-testid={`step-${s.id}`}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-ibm transition-all whitespace-nowrap ${
                    isActive ? 'bg-orange-500/15 text-orange-400 border border-orange-500/30'
                    : isDone ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 cursor-pointer hover:bg-emerald-500/20'
                    : 'bg-[#141414] text-[#525252] border border-[#1A1A1A]'
                  }`}>
                  {isDone ? <CheckCircle2 size={12} /> : <s.icon size={12} />}
                  <span className="hidden md:inline">{s.label}</span>
                  <span className="md:hidden">{s.id}</span>
                </button>
                {i < STEPS.length - 1 && <ChevronRight size={12} className="text-[#262626] mx-0.5 shrink-0" />}
              </div>
            );
          })}
        </div>
      </div>

      {/* ═══════ STEP 1: PROJECT SETUP + TOPIC ═══════ */}
      {step === 1 && (
        <div className="max-w-2xl mx-auto space-y-6" data-testid="step-1-content">
          <div className="text-center mb-6">
            <h2 className="font-outfit text-xl text-white font-semibold">Video Projenizi Oluşturun</h2>
            <p className="text-[#737373] font-ibm text-sm mt-1">Konuyu girin, AI senaryoyu otomatik yazsın</p>
          </div>

          {/* Topic - Main input */}
          <div>
            <label className="text-xs text-[#A3A3A3] font-ibm mb-1.5 block">Video Konusu *</label>
            <textarea value={aiTopic} onChange={e => setAiTopic(e.target.value)} data-testid="ai-topic-input"
              placeholder="Örn: Bir kahve dükkanının sabah rutini, İstanbul'da gün batımı turu, ürün tanıtım videosu..."
              className="w-full bg-[#141414] border border-[#262626] rounded-xl text-white text-sm font-ibm p-4 min-h-[80px] resize-y placeholder:text-[#404040] focus:outline-none focus:border-orange-500/40" />
          </div>

          {/* Scene count */}
          <div>
            <label className="text-xs text-[#A3A3A3] font-ibm mb-2 block">Sahne Sayısı</label>
            <div className="grid grid-cols-4 gap-2">
              {[3, 4, 5, 6].map(n => (
                <button key={n} onClick={() => { setSceneCount(n); setCustomCount(''); }} data-testid={`count-${n}`}
                  className={`p-3 rounded-xl border text-center transition-all ${
                    sceneCount === n && !customCount
                      ? 'bg-orange-500/15 border-orange-500/40 text-orange-400'
                      : 'bg-[#141414] border-[#262626] text-[#A3A3A3] hover:border-[#404040]'
                  }`}>
                  <span className="text-xl font-outfit font-bold block">{n}</span>
                  <span className="text-[10px] font-ibm block">{n * CREDIT_PER_SCENE} kredi</span>
                </button>
              ))}
            </div>
            <div className="flex items-center gap-3 mt-2">
              <span className="text-xs text-[#525252] font-ibm">veya</span>
              <Input type="number" min={2} max={10} value={customCount}
                onChange={e => { setCustomCount(e.target.value); setSceneCount(0); }}
                placeholder="Özel (2-10)" data-testid="custom-count-input"
                className="bg-[#141414] border-[#262626] text-white h-8 w-32 text-xs" />
            </div>
          </div>

          {/* Row: Tone + Style + Ratio */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-[#737373] font-ibm mb-1.5 block">Ton</label>
              <Select value={aiTone} onValueChange={setAiTone}>
                <SelectTrigger className="bg-[#141414] border-[#262626] text-white h-9 text-xs" data-testid="ai-tone-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#141414] border-[#262626]">{TONES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-[#737373] font-ibm mb-1.5 block">Stil</label>
              <Select value={aiStyle} onValueChange={setAiStyle}>
                <SelectTrigger className="bg-[#141414] border-[#262626] text-white h-9 text-xs" data-testid="ai-style-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#141414] border-[#262626]">{STYLES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-[#737373] font-ibm mb-1.5 block">Oran</label>
              <Select value={aspectRatio} onValueChange={setAspectRatio}>
                <SelectTrigger className="bg-[#141414] border-[#262626] text-white h-9 text-xs" data-testid="ratio-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#141414] border-[#262626]">{ASPECT_RATIOS.map(a => <SelectItem key={a.id} value={a.id}>{a.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          {/* Extra instructions */}
          <div>
            <label className="text-xs text-[#737373] font-ibm mb-1.5 block">Ek Talimatlar (opsiyonel)</label>
            <Input value={aiInstructions} onChange={e => setAiInstructions(e.target.value)} data-testid="ai-instructions-input"
              placeholder="Örn: ürün odaklı, her sahne farklı açıdan..."
              className="bg-[#141414] border-[#262626] text-white h-9 text-xs" />
          </div>

          <Button onClick={handleCreateAndGenerate} disabled={loading || generatingAI || !aiTopic.trim()} data-testid="create-project-btn"
            className="w-full bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-400 hover:to-amber-400 text-black font-ibm font-semibold h-12 text-sm">
            {loading || generatingAI
              ? <><Loader2 size={16} className="animate-spin mr-2" /> {generatingAI ? 'AI senaryo yazıyor...' : 'Proje oluşturuluyor...'}</>
              : <><Sparkles size={16} className="mr-2" /> Projeyi Oluştur ve Senaryoyu Yaz</>}
          </Button>
        </div>
      )}

      {/* ═══════ STEP 2: AI SCRIPT (EDITABLE) ═══════ */}
      {step === 2 && project && (
        <div className="space-y-4" data-testid="step-2-content">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-outfit text-lg text-white">AI Senaryo</h2>
              <p className="text-[#525252] font-ibm text-xs mt-0.5">Sahneleri düzenleyebilir, model ve süre değiştirebilirsiniz</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleAddScene}
                className="border-[#262626] text-[#A3A3A3] h-8 text-xs" data-testid="add-scene-btn">
                <Plus size={12} className="mr-1" /> Sahne Ekle
              </Button>
              <Button size="sm" onClick={() => { saveScenes(); setStep(3); }}
                className="bg-orange-500 hover:bg-orange-400 text-black h-8 text-xs font-semibold" data-testid="next-to-review-btn">
                İncelemeye Git <ChevronRight size={12} className="ml-1" />
              </Button>
            </div>
          </div>

          {scenes.map((scene, idx) => (
            <Card key={scene.id} className="bg-[#141414] border-[#262626]" data-testid={`scene-card-${idx}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-lg bg-orange-500/15 flex items-center justify-center">
                      <span className="text-xs font-outfit font-bold text-orange-400">{idx + 1}</span>
                    </div>
                    <Input value={scene.title} onChange={e => updateScene(scene.id, 'title', e.target.value)}
                      placeholder={`Sahne ${idx + 1}`} data-testid={`scene-title-${idx}`}
                      className="bg-transparent border-none text-white font-ibm font-medium text-sm h-7 p-0 w-56 focus:ring-0" />
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={() => handleDuplicate(scene.id)}
                      className="h-7 w-7 p-0 text-[#525252] hover:text-blue-400" data-testid={`dup-scene-${idx}`}><Copy size={12} /></Button>
                    {scenes.length > 2 && (
                      <Button variant="ghost" size="sm" onClick={() => handleRemoveScene(scene.id)}
                        className="h-7 w-7 p-0 text-[#525252] hover:text-red-400" data-testid={`del-scene-${idx}`}><Trash2 size={12} /></Button>
                    )}
                  </div>
                </div>

                {/* Prompt */}
                <textarea value={scene.prompt} onChange={e => updateScene(scene.id, 'prompt', e.target.value)}
                  placeholder="Sahne açıklaması / video prompt'u..."
                  data-testid={`scene-prompt-${idx}`}
                  className="w-full bg-[#0A0A0A] border border-[#262626] rounded-lg text-white text-sm font-ibm p-3 min-h-[64px] resize-y placeholder:text-[#404040] focus:outline-none focus:border-orange-500/40" />

                {/* Options row */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3">
                  <Select value={scene.motion} onValueChange={v => updateScene(scene.id, 'motion', v)}>
                    <SelectTrigger className="bg-[#0A0A0A] border-[#262626] text-white h-8 text-xs" data-testid={`scene-motion-${idx}`}><SelectValue placeholder="Hareket" /></SelectTrigger>
                    <SelectContent className="bg-[#141414] border-[#262626]">{MOTIONS.map(m => <SelectItem key={m.id} value={m.id}>{m.label}</SelectItem>)}</SelectContent>
                  </Select>
                  <Select value={String(scene.duration)} onValueChange={v => updateScene(scene.id, 'duration', parseInt(v))}>
                    <SelectTrigger className="bg-[#0A0A0A] border-[#262626] text-white h-8 text-xs" data-testid={`scene-duration-${idx}`}><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#141414] border-[#262626]">
                      <SelectItem value="5">5 saniye</SelectItem>
                      <SelectItem value="10">10 saniye</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={scene.model_id} onValueChange={v => {
                    const m = MODELS.find(x => x.model_id === v);
                    if (m) { updateScene(scene.id, 'model', m.id); updateScene(scene.id, 'model_id', v); }
                  }}>
                    <SelectTrigger className="bg-[#0A0A0A] border-[#262626] text-white h-8 text-xs" data-testid={`scene-model-${idx}`}><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#141414] border-[#262626]">
                      {MODELS.map(m => (
                        <SelectItem key={m.model_id} value={m.model_id} disabled={!m.available}>
                          {m.label}{!m.available ? ' (Yakında)' : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button variant="ghost" size="sm" onClick={() => handleRegenScenePrompt(scene.id)}
                    disabled={regeneratingScene === scene.id}
                    className="h-8 text-xs text-[#525252] hover:text-orange-400" data-testid={`regen-prompt-${idx}`}>
                    {regeneratingScene === scene.id ? <Loader2 size={12} className="animate-spin" /> : <><RefreshCw size={12} className="mr-1" /> Yeniden Yaz</>}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* ═══════ STEP 3: REVIEW ═══════ */}
      {step === 3 && project && (
        <div className="space-y-4" data-testid="step-3-content">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-outfit text-lg text-white">Sahne İnceleme</h2>
              <p className="text-[#525252] font-ibm text-xs mt-0.5">Üretim öncesi son kontrol</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setStep(2)} className="border-[#262626] text-[#A3A3A3] h-8 text-xs">
                <ChevronLeft size={12} className="mr-1" /> Düzenle
              </Button>
              <Button size="sm" onClick={handleGenerateAll} disabled={generatingVideos || !scenes.some(s => s.prompt)}
                data-testid="start-generation-btn"
                className="bg-orange-500 hover:bg-orange-400 text-black h-8 text-xs font-semibold">
                <Zap size={12} className="mr-1" /> Videoları Üret ({scenes.filter(s => s.prompt && s.status !== 'done').length} sahne)
              </Button>
            </div>
          </div>

          {/* Credit summary */}
          <div className="p-3 bg-orange-500/5 border border-orange-500/20 rounded-lg flex items-center justify-between">
            <span className="text-xs text-[#A3A3A3] font-ibm">Toplam Maliyet</span>
            <span className="text-sm font-outfit font-bold text-orange-400">{scenes.filter(s => s.prompt).length * CREDIT_PER_SCENE} kredi</span>
          </div>

          {scenes.map((scene, idx) => (
            <div key={scene.id} className={`p-4 rounded-xl border ${scene.prompt ? 'bg-[#141414] border-[#262626]' : 'bg-[#0D0D0D] border-[#1A1A1A] opacity-50'}`}
              data-testid={`review-scene-${idx}`}>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-orange-500/15 flex items-center justify-center shrink-0 mt-0.5">
                  <span className="text-xs font-outfit font-bold text-orange-400">{idx + 1}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white font-ibm font-medium">{scene.title || `Sahne ${idx + 1}`}</p>
                  <p className="text-xs text-[#737373] font-ibm mt-1 line-clamp-2">{scene.prompt || 'Prompt girilmedi'}</p>
                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    <Badge variant="outline" className="text-[10px] bg-[#0A0A0A] border-[#262626] text-[#737373]">{scene.motion}</Badge>
                    <Badge variant="outline" className="text-[10px] bg-[#0A0A0A] border-[#262626] text-[#737373]">{scene.duration}s</Badge>
                    <Badge variant="outline" className="text-[10px] bg-[#0A0A0A] border-[#262626] text-[#737373]">{MODELS.find(m => m.model_id === scene.model_id)?.label || scene.model_id}</Badge>
                    <Badge variant="outline" className="text-[10px] bg-[#0A0A0A] border-[#262626] text-[#737373]">{CREDIT_PER_SCENE} kredi</Badge>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ═══════ STEP 4: GENERATION PROGRESS ═══════ */}
      {step === 4 && project && (
        <div className="space-y-4" data-testid="step-4-content">
          <div className="text-center mb-6">
            <h2 className="font-outfit text-xl text-white font-semibold">Video Üretimi</h2>
            <p className="text-[#737373] font-ibm text-sm mt-1">Sahneler üretiliyor, lütfen bekleyin...</p>
          </div>

          <div className="grid grid-cols-3 gap-3 max-w-lg mx-auto">
            <div className="text-center p-3 bg-[#141414] rounded-xl border border-[#262626]">
              <span className="text-xl font-outfit font-bold text-white">{scenes.length}</span>
              <p className="text-[10px] text-[#525252] font-ibm mt-0.5">Toplam</p>
            </div>
            <div className="text-center p-3 bg-emerald-500/5 rounded-xl border border-emerald-500/20">
              <span className="text-xl font-outfit font-bold text-emerald-400">{completedScenes.length}</span>
              <p className="text-[10px] text-[#525252] font-ibm mt-0.5">Tamamlanan</p>
            </div>
            <div className="text-center p-3 bg-orange-500/5 rounded-xl border border-orange-500/20">
              <span className="text-xl font-outfit font-bold text-orange-400">{generatingScenes.length}</span>
              <p className="text-[10px] text-[#525252] font-ibm mt-0.5">Devam Eden</p>
            </div>
          </div>

          {scenes.map((scene, idx) => (
            <div key={scene.id} className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${
              scene.status === 'done' ? 'bg-emerald-500/5 border-emerald-500/20'
              : scene.status === 'failed' ? 'bg-red-500/5 border-red-500/20'
              : scene.status === 'generating' ? 'bg-orange-500/5 border-orange-500/20'
              : 'bg-[#141414] border-[#262626]'
            }`} data-testid={`gen-scene-${idx}`}>
              <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0">
                {scene.status === 'done' ? <CheckCircle2 size={18} className="text-emerald-400" />
                : scene.status === 'failed' ? <AlertCircle size={18} className="text-red-400" />
                : scene.status === 'generating' ? <Loader2 size={18} className="text-orange-400 animate-spin" />
                : <Clock size={14} className="text-[#525252]" />}
              </div>
              <div className="flex-1">
                <p className="text-sm text-white font-ibm">{scene.title || `Sahne ${idx + 1}`}</p>
                <p className="text-[10px] text-[#525252] font-ibm">
                  {scene.status === 'done' ? 'Tamamlandı' : scene.status === 'failed' ? (scene.error?.slice(0, 60) || 'Hata') : scene.status === 'generating' ? 'Üretiliyor...' : 'Beklemede'}
                </p>
              </div>
              <Badge variant="outline" className={`text-[10px] ${
                scene.status === 'done' ? 'text-emerald-400 border-emerald-500/20'
                : scene.status === 'failed' ? 'text-red-400 border-red-500/20'
                : scene.status === 'generating' ? 'text-orange-400 border-orange-500/20'
                : 'text-[#525252] border-[#262626]'
              }`}>
                {MODELS.find(m => m.model_id === scene.model_id)?.label || scene.model}
              </Badge>
            </div>
          ))}

          {!generatingVideos && (
            <Button onClick={() => setStep(5)} className="w-full bg-orange-500 hover:bg-orange-400 text-black h-10 text-sm font-semibold" data-testid="go-to-results-btn">
              Sonuçları Gör <ArrowRight size={14} className="ml-1" />
            </Button>
          )}
        </div>
      )}

      {/* ═══════ STEP 5: RESULTS ═══════ */}
      {step === 5 && project && (
        <div className="space-y-4" data-testid="step-5-content">
          <div className="flex items-center justify-between">
            <h2 className="font-outfit text-lg text-white">Sahne Sonuçları</h2>
            <Button size="sm" onClick={() => setStep(6)} className="bg-orange-500 hover:bg-orange-400 text-black h-8 text-xs font-semibold" data-testid="go-to-reorder-btn">
              Sıralama <ChevronRight size={12} className="ml-1" />
            </Button>
          </div>

          {scenes.map((scene, idx) => (
            <Card key={scene.id} className="bg-[#141414] border-[#262626]" data-testid={`result-scene-${idx}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-lg bg-orange-500/15 flex items-center justify-center">
                      <span className="text-xs font-outfit font-bold text-orange-400">{idx + 1}</span>
                    </div>
                    <span className="text-sm text-white font-ibm font-medium">{scene.title || `Sahne ${idx + 1}`}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-[10px] text-[#525252] border-[#262626]">{scene.duration}s</Badge>
                    <Badge variant="outline" className="text-[10px] text-[#525252] border-[#262626]">{MODELS.find(m => m.model_id === scene.model_id)?.label || scene.model}</Badge>
                  </div>
                </div>

                {scene.status === 'done' && scene.video_url ? (
                  <div className="space-y-3">
                    <video src={`${backendUrl}${scene.video_url}`} controls className="w-full rounded-lg border border-[#262626] max-h-[240px]" data-testid={`video-player-${idx}`} />
                    <div className="flex gap-2">
                      <Input value={editPrompts[scene.id] || ''} onChange={e => setEditPrompts(prev => ({ ...prev, [scene.id]: e.target.value }))}
                        placeholder='"daha hareketli yap", "kamerayı yakınlaştır"...'
                        data-testid={`edit-prompt-${idx}`}
                        className="bg-[#0A0A0A] border-[#262626] text-white h-8 text-xs flex-1" />
                      <Button size="sm" onClick={() => handleRegenerateVideo(scene.id, editPrompts[scene.id] || '')}
                        disabled={regeneratingScene === scene.id}
                        className="bg-[#262626] hover:bg-[#333] text-white h-8 text-xs" data-testid={`regen-scene-${idx}`}>
                        {regeneratingScene === scene.id ? <Loader2 size={12} className="animate-spin" /> : <><Wand2 size={12} className="mr-1" /> Yeniden Üret</>}
                      </Button>
                    </div>
                  </div>
                ) : scene.status === 'failed' ? (
                  <div className="p-3 bg-red-500/5 rounded-lg border border-red-500/20">
                    <p className="text-xs text-red-400 font-ibm">{scene.error || 'Üretim başarısız'}</p>
                    <Button size="sm" onClick={() => handleRegenerateVideo(scene.id)} disabled={regeneratingScene === scene.id}
                      className="mt-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 h-7 text-xs" data-testid={`retry-scene-${idx}`}>
                      <RefreshCw size={12} className="mr-1" /> Tekrar Dene
                    </Button>
                  </div>
                ) : scene.status === 'generating' ? (
                  <div className="flex items-center gap-2 p-3 bg-orange-500/5 rounded-lg">
                    <Loader2 size={14} className="text-orange-400 animate-spin" />
                    <span className="text-xs text-orange-400 font-ibm">Üretiliyor...</span>
                  </div>
                ) : (
                  <p className="text-xs text-[#525252] font-ibm p-3">Henüz üretilmedi</p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* ═══════ STEP 6: REORDER ═══════ */}
      {step === 6 && project && (
        <div className="space-y-4" data-testid="step-6-content">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-outfit text-lg text-white">Sahne Sıralaması</h2>
              <p className="text-[#525252] font-ibm text-xs mt-0.5">Sürükle-bırak ile sahneleri sıralayın</p>
            </div>
            <Button size="sm" onClick={() => setStep(7)} className="bg-orange-500 hover:bg-orange-400 text-black h-8 text-xs font-semibold" data-testid="go-to-merge-btn">
              Birleştirmeye Git <ChevronRight size={12} className="ml-1" />
            </Button>
          </div>

          {scenes.map((scene, idx) => (
            <div key={scene.id} draggable onDragStart={() => handleDragStart(idx)} onDragOver={handleDragOver}
              onDrop={() => handleDrop(idx)}
              className={`flex items-center gap-3 p-3 rounded-xl border transition-all cursor-grab active:cursor-grabbing ${
                dragIdx === idx ? 'border-orange-500/40 bg-orange-500/5' : 'bg-[#141414] border-[#262626] hover:border-[#404040]'
              }`} data-testid={`reorder-scene-${idx}`}>
              <GripVertical size={16} className="text-[#525252] shrink-0" />
              <div className="w-7 h-7 rounded-lg bg-orange-500/15 flex items-center justify-center shrink-0">
                <span className="text-xs font-outfit font-bold text-orange-400">{idx + 1}</span>
              </div>
              {scene.video_url && <video src={`${backendUrl}${scene.video_url}`} className="w-20 h-12 rounded object-cover border border-[#262626]" muted />}
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white font-ibm truncate">{scene.title || `Sahne ${idx + 1}`}</p>
                <p className="text-[10px] text-[#525252] font-ibm truncate">{scene.prompt?.slice(0, 60)}</p>
              </div>
              <Badge variant="outline" className="text-[10px] text-[#525252] border-[#262626]">{scene.duration}s</Badge>
              {scene.status === 'done' && <CheckCircle2 size={14} className="text-emerald-400" />}
            </div>
          ))}
        </div>
      )}

      {/* ═══════ STEP 7: MERGE ═══════ */}
      {step === 7 && project && (
        <div className="max-w-2xl mx-auto space-y-6" data-testid="step-7-content">
          <div className="text-center mb-4">
            <h2 className="font-outfit text-xl text-white font-semibold">Videoları Birleştir</h2>
            <p className="text-[#737373] font-ibm text-sm mt-1">Tüm sahneler tek bir videoya birleştirilecek</p>
          </div>

          <div className="space-y-2">
            {scenes.filter(s => s.status === 'done').map((scene, idx) => (
              <div key={scene.id} className="flex items-center gap-3 p-2 bg-[#141414] rounded-lg border border-[#262626]">
                <span className="text-xs font-outfit font-bold text-orange-400 w-6">{idx + 1}</span>
                <span className="text-xs text-white font-ibm flex-1 truncate">{scene.title}</span>
                <span className="text-[10px] text-[#525252] font-jetbrains">{scene.duration}s</span>
                <CheckCircle2 size={12} className="text-emerald-400" />
              </div>
            ))}
          </div>

          <div className="p-4 bg-[#141414] rounded-xl border border-[#262626]">
            <div className="flex items-center justify-between">
              <span className="text-sm text-[#A3A3A3] font-ibm">Tahmini Süre</span>
              <span className="text-lg font-outfit font-bold text-white">{completedScenes.reduce((a, s) => a + (s.duration || 5), 0)} saniye</span>
            </div>
            <div className="flex items-center justify-between mt-1">
              <span className="text-sm text-[#A3A3A3] font-ibm">Sahne Sayısı</span>
              <span className="text-lg font-outfit font-bold text-white">{completedScenes.length}</span>
            </div>
          </div>

          {completedScenes.length < 2 ? (
            <div className="p-4 bg-red-500/5 rounded-xl border border-red-500/20 text-center">
              <AlertCircle size={20} className="mx-auto text-red-400 mb-2" />
              <p className="text-sm text-red-400 font-ibm">En az 2 tamamlanmış sahne gerekli</p>
              <Button size="sm" onClick={() => setStep(5)} className="mt-2 bg-red-500/20 text-red-400 h-8 text-xs">Sonuçlara Dön</Button>
            </div>
          ) : (
            <Button onClick={handleMerge} disabled={merging} data-testid="merge-btn"
              className="w-full bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-400 hover:to-amber-400 text-black font-ibm font-semibold h-12 text-sm">
              {merging ? <><Loader2 size={16} className="animate-spin mr-2" /> Birleştiriliyor...</> : <><Merge size={16} className="mr-2" /> Videoları Birleştir</>}
            </Button>
          )}
        </div>
      )}

      {/* ═══════ STEP 8: FINAL VIDEO ═══════ */}
      {step === 8 && project && (
        <div className="max-w-3xl mx-auto space-y-6" data-testid="step-8-content">
          <div className="text-center mb-4">
            <h2 className="font-outfit text-xl text-white font-semibold">Final Video</h2>
            <p className="text-[#737373] font-ibm text-sm mt-1">Videonuz hazır!</p>
          </div>

          {project.final_video_url ? (
            <div className="space-y-4">
              <div className="rounded-xl border border-[#262626] overflow-hidden bg-black">
                <video src={`${backendUrl}${project.final_video_url}`} controls className="w-full max-h-[480px]" data-testid="final-video-player" />
              </div>
              <div className="flex gap-3">
                <a href={`${backendUrl}${project.final_video_url}`} download
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-orange-500 hover:bg-orange-400 text-black rounded-xl font-ibm font-semibold text-sm transition-colors"
                  data-testid="download-btn">
                  <Download size={16} /> İndir
                </a>
                <Button onClick={handleSaveToLibrary} className="flex-1 bg-[#262626] hover:bg-[#333] text-white h-auto py-3 text-sm" data-testid="save-library-btn">
                  <Save size={16} className="mr-2" /> Kütüphaneye Kaydet
                </Button>
              </div>
              <div className="p-4 bg-[#141414] rounded-xl border border-[#262626]">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <span className="text-xs text-[#525252] font-ibm block">Sahne</span>
                    <span className="text-lg font-outfit font-bold text-white">{scenes.length}</span>
                  </div>
                  <div>
                    <span className="text-xs text-[#525252] font-ibm block">Süre</span>
                    <span className="text-lg font-outfit font-bold text-white">{completedScenes.reduce((a, s) => a + (s.duration || 5), 0)}s</span>
                  </div>
                  <div>
                    <span className="text-xs text-[#525252] font-ibm block">Oran</span>
                    <span className="text-lg font-outfit font-bold text-white">{project.aspect_ratio}</span>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-[#525252] font-ibm">Henüz birleştirilmiş video yok</p>
              <Button onClick={() => setStep(7)} className="mt-3 bg-[#262626] text-white h-8 text-xs">Birleştirmeye Git</Button>
            </div>
          )}

          <Button variant="outline" onClick={() => setStep(9)} className="w-full border-[#262626] text-[#A3A3A3] h-9 text-xs">
            Son İşlemler <ChevronRight size={12} className="ml-1" />
          </Button>
        </div>
      )}

      {/* ═══════ STEP 9: FINAL EDIT OPTIONS ═══════ */}
      {step === 9 && project && (
        <div className="max-w-2xl mx-auto space-y-6" data-testid="step-9-content">
          <div className="text-center mb-4">
            <h2 className="font-outfit text-xl text-white font-semibold">Son Düzenlemeler</h2>
            <p className="text-[#737373] font-ibm text-sm mt-1">Videonuzda değişiklik yapabilirsiniz</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <button onClick={() => setStep(5)} data-testid="back-to-results"
              className="p-4 bg-[#141414] border border-[#262626] rounded-xl text-left hover:border-[#404040] transition-colors">
              <RefreshCw size={20} className="text-orange-400 mb-2" />
              <p className="text-sm text-white font-ibm font-medium">Sahneleri Yeniden Üret</p>
              <p className="text-xs text-[#525252] font-ibm mt-0.5">Belirli sahneleri değiştirin veya yeniden oluşturun</p>
            </button>
            <button onClick={() => setStep(6)} data-testid="back-to-reorder"
              className="p-4 bg-[#141414] border border-[#262626] rounded-xl text-left hover:border-[#404040] transition-colors">
              <ArrowUpDown size={20} className="text-blue-400 mb-2" />
              <p className="text-sm text-white font-ibm font-medium">Sıralama Değiştir</p>
              <p className="text-xs text-[#525252] font-ibm mt-0.5">Sahnelerin sırasını değiştirin</p>
            </button>
            <button onClick={() => setStep(7)} data-testid="back-to-merge"
              className="p-4 bg-[#141414] border border-[#262626] rounded-xl text-left hover:border-[#404040] transition-colors">
              <Merge size={20} className="text-emerald-400 mb-2" />
              <p className="text-sm text-white font-ibm font-medium">Yeniden Birleştir</p>
              <p className="text-xs text-[#525252] font-ibm mt-0.5">Değişikliklerden sonra tekrar birleştirin</p>
            </button>
            <button onClick={() => { if (project.final_video_url) setStep(8); else toast.info('Önce birleştirme yapın'); }} data-testid="back-to-final"
              className="p-4 bg-[#141414] border border-[#262626] rounded-xl text-left hover:border-[#404040] transition-colors">
              <MonitorPlay size={20} className="text-amber-400 mb-2" />
              <p className="text-sm text-white font-ibm font-medium">Final Videoyu Göster</p>
              <p className="text-xs text-[#525252] font-ibm mt-0.5">Birleştirilmiş videoyu izleyin ve indirin</p>
            </button>
          </div>

          {project.final_video_url && (
            <div className="flex gap-3 pt-4">
              <a href={`${backendUrl}${project.final_video_url}`} download
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-orange-500 hover:bg-orange-400 text-black rounded-xl font-ibm font-semibold text-sm transition-colors" data-testid="final-download-btn">
                <Download size={16} /> Videoyu İndir
              </a>
              <Button onClick={handleSaveToLibrary} className="flex-1 bg-[#262626] hover:bg-[#333] text-white h-auto py-3 text-sm" data-testid="final-save-btn">
                <Save size={16} className="mr-2" /> Kütüphaneye Kaydet
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
